import dayjs from 'dayjs';
import { UpdateChangeKeys, ChangeTypes, RowAddChangeKeys, RowRemoveChangeKeys } from 'enums/autosave/ChangeKeys';
import { Change, RowAddChange, RowRemoveChange, SavedChanges, UpdateChange } from 'interfaces/autosave/Changes';
import { makeAutoObservable } from 'mobx';
import { notify } from 'services/NotificationService';

export class AutosaveStore {
  private id = 'autosave';

  private savedChangesQueue: SavedChanges = {
    sessionId: '',
    changes: [],
    lastUpdateTimestamp: null,
  };

  private autosaveInterval = 5000;

  private interval: ReturnType<typeof setInterval> | ReturnType<typeof clearInterval> = undefined;

  constructor() {
    this.savedChangesQueue = this.load();
    makeAutoObservable(this);
  }

  pushToLocalStorage = () => {
    try {
      localStorage.setItem(this.id, JSON.stringify(this.savedChangesQueue));
    } catch (err: unknown) {
      if (err instanceof Error) {
        notify('Error', err.message, 'error');
      }
    }
  };

  manuallyPushToLocalStorage = () => {
    this.savedChangesQueue.lastUpdateTimestamp = dayjs();
    this.pushToLocalStorage();
  };

  isUpdateChange = (change: Change): change is UpdateChange => {
    return change[UpdateChangeKeys.Type] === ChangeTypes.Update;
  };

  isRowAddChange = (change: Change): change is RowAddChange => {
    return change[RowAddChangeKeys.Type] === ChangeTypes.AddAbove || change[RowAddChangeKeys.Type] === ChangeTypes.AddBelow;
  };

  isRowRemoveChange = (change: Change): change is RowRemoveChange => {
    return change[RowRemoveChangeKeys.Type] === ChangeTypes.Remove;
  };

  save = (sessionId: string, newChanges: Change): void => {
    this.savedChangesQueue.sessionId = sessionId;
    this.savedChangesQueue.lastUpdateTimestamp = dayjs();
    const duplicatedChangeIndex = this.savedChangesQueue.changes.findIndex(
      (existingChange) =>
        this.isUpdateChange(existingChange) &&
        this.isUpdateChange(newChanges) &&
        existingChange[UpdateChangeKeys.DataKey] === newChanges[UpdateChangeKeys.DataKey] &&
        existingChange[UpdateChangeKeys.RowId] === newChanges[UpdateChangeKeys.RowId]
    );
    if (duplicatedChangeIndex !== -1) {
      this.savedChangesQueue.changes[duplicatedChangeIndex] = newChanges;
    } else {
      this.savedChangesQueue.changes.push(newChanges);
    }
  };

  load = (): SavedChanges => {
    const savedChanges = localStorage.getItem(this.id);
    if (savedChanges) {
      return JSON.parse(savedChanges);
    }
    return { sessionId: '', changes: [], lastUpdateTimestamp: null };
  };

  clear = (): void => {
    this.savedChangesQueue = { sessionId: '', changes: [], lastUpdateTimestamp: null };
    this.pushToLocalStorage();
  };

  start = (): void => {
    this.interval = setInterval(() => {
      this.pushToLocalStorage();
    }, this.autosaveInterval);
  };

  stop = (): void => {
    if (this.interval) {
      this.interval = clearInterval(this.interval);
    }
  };
}
